import { useHotkeySequence } from "@tanstack/react-hotkeys"
import { useRouter } from "@tanstack/react-router"

import { navigationShortcutsFor } from "~/constants/hotkeys"
import { useSidebarItems } from "~/hooks/useSidebarItems"
import useShowHelix from "~/hooks/useShowHelix"
import { trackEvent } from "~/hooks/useTrackingService"
import { shortcutMeta } from "~/utils/shortcutCheatsheet"

// Both variants are module-level and the same length, so the hook count below is stable
// when showHelix flips.
const SHORTCUTS_BY_VARIANT = {
  default: navigationShortcutsFor({ showHelix: false }),
  helix: navigationShortcutsFor({ showHelix: true }),
}

export function useNavigationHotkeys() {
  const router = useRouter()
  const { flattenedSidebarItems } = useSidebarItems()
  const showHelix = useShowHelix()
  const shortcuts = showHelix ? SHORTCUTS_BY_VARIANT.helix : SHORTCUTS_BY_VARIANT.default

  const sidebarNames = new Map(
    flattenedSidebarItems
      .filter((item) => !item.disabled && !item.isButton)
      .map((item) => [item.pathName, item.name]),
  )

  for (const { sequence, path } of shortcuts) {
    const name = sidebarNames.get(path)
    // Enabled only for pages the sidebar offers, so G sequences follow its flags and permissions
    // eslint-disable-next-line react-hooks/rules-of-hooks -- both variants are module-level consts of equal length, hook count is stable across renders
    useHotkeySequence(
      [...sequence],
      () => {
        trackEvent("Navigation Shortcut Used", { Path: path })
        router.navigate({ to: path })
      },
      {
        ignoreInputs: true,
        enabled: name !== undefined,
        meta: shortcutMeta("Navigation", `Go to ${name ?? path}`),
      },
    )
  }
}
